import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, Circle, Sparkles } from "lucide-react";

import { toast } from "@/components/ui/sonner";
import { fetchAtlasNodes } from "@/lib/api";
import { pickText } from "@/lib/content";

// Timeline flow from Kether to Malkuth (Tree of Life descent)
const SEPHIROTH_ORDER = [
  { slug: "kether", pillar: "middle", level: 1 },
  { slug: "chokmah", pillar: "right", level: 2 },
  { slug: "binah", pillar: "left", level: 2 },
  { slug: "chesed", pillar: "right", level: 3 },
  { slug: "geburah", pillar: "left", level: 3 },
  { slug: "tiphereth", pillar: "middle", level: 4 },
  { slug: "netzach", pillar: "right", level: 5 },
  { slug: "hod", pillar: "left", level: 5 },
  { slug: "yesod", pillar: "middle", level: 6 },
  { slug: "malkuth", pillar: "middle", level: 7 },
];

const PILLAR_COLORS = {
  left: { bg: "from-blue-900/30 to-blue-950/10", border: "border-blue-400/30", name: "Severity" },
  middle: { bg: "from-amber-900/30 to-amber-950/10", border: "border-primary/30", name: "Equilibrium" },
  right: { bg: "from-red-900/30 to-red-950/10", border: "border-red-400/30", name: "Mercy" },
};

export const TimelinePage = ({ language, vipEmail }) => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    const loadNodes = async () => {
      try {
        setLoading(true);
        const data = await fetchAtlasNodes();
        setNodes(data);
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load timeline.", "Impossibile caricare la timeline."));
      } finally {
        setLoading(false);
      }
    };
    loadNodes();
  }, [language]);

  const getNodeData = (slug) => {
    return nodes.find((n) => n.slug === slug);
  };

  const groupedByLevel = SEPHIROTH_ORDER.reduce((acc, item) => {
    if (!acc[item.level]) acc[item.level] = [];
    acc[item.level].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-10" data-testid="timeline-page">
      <div className="max-w-3xl space-y-4">
        <p className="section-label" data-testid="timeline-eyebrow">
          {pickText(language, "Timeline of Descent", "Timeline della Discesa")}
        </p>
        <h2 className="text-5xl text-foreground md:text-6xl" data-testid="timeline-title">
          {pickText(language, "From the crown to the kingdom.", "Dalla corona al regno.")}
        </h2>
        <p className="body-copy" data-testid="timeline-description">
          {pickText(language, "Follow the path of divine emanation from Kether to Malkuth through the three pillars of the Tree of Life.", "Segui il percorso dell'emanazione divina da Kether a Malkuth attraverso i tre pilastri dell'Albero della Vita.")}
        </p>
      </div>

      {/* Three Pillars Legend */}
      <div className="flex flex-wrap justify-center gap-6">
        {Object.entries(PILLAR_COLORS).map(([key, value]) => (
          <div
            className={`flex items-center gap-2 rounded-full border bg-gradient-to-r px-4 py-2 ${value.border} ${value.bg}`}
            key={key}
          >
            <Circle className="h-3 w-3 fill-current" />
            <span className="text-xs uppercase tracking-[0.2em] text-slate-300">
              {pickText(language, 
                key === "left" ? "Pillar of Severity" : key === "right" ? "Pillar of Mercy" : "Middle Pillar",
                key === "left" ? "Pilastro della Severità" : key === "right" ? "Pilastro della Misericordia" : "Pilastro Centrale"
              )}
            </span>
          </div>
        ))}
      </div>

      {/* Timeline Visualization */}
      <div className="glass-card rounded-[2rem] p-6 md:p-10" data-testid="timeline-visualization">
        {loading ? (
          <div className="rounded-3xl border border-white/10 bg-black/20 p-6 text-center text-sm text-slate-300">
            {pickText(language, "Loading timeline...", "Caricamento timeline...")}
          </div>
        ) : (
          <div className="relative mx-auto max-w-4xl space-y-4">
            {/* Central pillar line */}
            <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gradient-to-b from-primary/60 via-primary/30 to-primary/60" />

            {Object.entries(groupedByLevel).map(([level, items], levelIndex) => (
              <div key={level}>
                {/* Level row */}
                <div className="relative flex items-center justify-center gap-4 py-4 md:gap-8">
                  {items.map((item) => {
                    const nodeData = getNodeData(item.slug);
                    const pillarStyle = PILLAR_COLORS[item.pillar];
                    const isSelected = selectedNode === item.slug;

                    if (!nodeData) return null;

                    return (
                      <Link
                        className={`group relative flex flex-col items-center transition-all duration-300 ${
                          item.pillar === "left" ? "mr-auto" : item.pillar === "right" ? "ml-auto" : ""
                        }`}
                        data-testid={`timeline-node-${item.slug}`}
                        key={item.slug}
                        to={`/archive?entry=${item.slug}`}
                        onMouseEnter={() => setSelectedNode(item.slug)}
                        onMouseLeave={() => setSelectedNode(null)}
                      >
                        {/* Node circle */}
                        <div
                          className={`flex h-16 w-16 items-center justify-center rounded-full border-2 bg-gradient-to-br transition-all duration-300 md:h-20 md:w-20 ${pillarStyle.border} ${pillarStyle.bg} ${
                            isSelected ? "scale-110 shadow-lg shadow-primary/20" : "group-hover:scale-105"
                          }`}
                        >
                          <Sparkles className={`h-6 w-6 md:h-8 md:w-8 ${isSelected ? "text-primary" : "text-slate-400"}`} />
                        </div>

                        {/* Node label */}
                        <div className="mt-3 text-center">
                          <p className={`text-sm font-medium transition-colors md:text-base ${isSelected ? "text-primary" : "text-foreground"}`}>
                            {pickText(language, nodeData.title_en, nodeData.title_it)}
                          </p>
                          <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-slate-500 md:text-xs">
                            {pickText(language, nodeData.preview_en, nodeData.preview_it)}
                          </p>
                        </div>

                        {/* Expanded info on hover */}
                        {isSelected && (
                          <div className="absolute -bottom-2 left-1/2 z-10 w-64 -translate-x-1/2 translate-y-full rounded-2xl border border-primary/20 bg-black/95 p-4 shadow-2xl">
                            <p className="text-xs text-slate-300">
                              {pickText(language, 
                                `Click to explore ${nodeData.title_en} in the archive.`,
                                `Clicca per esplorare ${nodeData.title_it} nell'archivio.`
                              )}
                            </p>
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>

                {/* Arrow between levels */}
                {levelIndex < Object.keys(groupedByLevel).length - 1 && (
                  <div className="flex justify-center py-2">
                    <ArrowDown className="h-6 w-6 text-primary/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Journey Info */}
      <div className="grid gap-6 md:grid-cols-3">
        <div className="glass-card rounded-[1.5rem] p-5">
          <p className="text-xs uppercase tracking-[0.24em] text-blue-400/80">
            {pickText(language, "Pillar of Severity", "Pilastro della Severità")}
          </p>
          <p className="mt-3 text-sm leading-7 text-slate-300">
            {pickText(language, "Binah, Geburah, Hod — the feminine, receptive, and form-giving aspects.", "Binah, Geburah, Hod — gli aspetti femminili, ricettivi e formanti.")}
          </p>
        </div>
        <div className="glass-card rounded-[1.5rem] border-primary/20 p-5">
          <p className="text-xs uppercase tracking-[0.24em] text-primary/80">
            {pickText(language, "Middle Pillar", "Pilastro Centrale")}
          </p>
          <p className="mt-3 text-sm leading-7 text-slate-300">
            {pickText(language, "Kether, Tiphereth, Yesod, Malkuth — the path of equilibrium and direct descent.", "Kether, Tiphereth, Yesod, Malkuth — il percorso di equilibrio e discesa diretta.")}
          </p>
        </div>
        <div className="glass-card rounded-[1.5rem] p-5">
          <p className="text-xs uppercase tracking-[0.24em] text-red-400/80">
            {pickText(language, "Pillar of Mercy", "Pilastro della Misericordia")}
          </p>
          <p className="mt-3 text-sm leading-7 text-slate-300">
            {pickText(language, "Chokmah, Chesed, Netzach — the masculine, expansive, and force-giving aspects.", "Chokmah, Chesed, Netzach — gli aspetti maschili, espansivi e di forza.")}
          </p>
        </div>
      </div>
    </div>
  );
};
